import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import config from '../../../config';
import { ProxyEndPoints } from '../config/endpoints';

export interface DisclaimerResponse {
  disclaimerLang: string;
  createdDate: number;
  disclaimerInfo: string;
  disclaimerSetting?: boolean;
  deleted?: boolean;
}

interface DisclaimerI18nFields {
  title: string;
  subHeading: string;
  content: string;
  buttonText: string;
  alertMessage: string;
}

interface DisclaimerI18nFile {
  disclaimer?: Partial<DisclaimerI18nFields>;
}

export interface ResolvedDisclaimer {
  disclaimerLang: string;
  title: string;
  subHeading: string;
  disclaimerInfo: string;
  buttonText: string;
  alertMessage: string;
  apiCreatedDate?: number;
}

@Injectable({ providedIn: 'root' })
export class DisclaimerAcceptanceService {
  private readonly http = inject(HttpClient);

  private readonly acceptanceStorageKey = 'azAcceptTerms';
  private readonly i18nPath = '/assets/i18n';

  private readonly defaultFields: DisclaimerI18nFields = {
    title: 'AZ Translate',
    subHeading: 'End User License Agreement',
    content: '',
    buttonText: 'Agree',
    alertMessage: 'Scroll to the bottom before accepting the EULA.',
  };

  getResolvedDisclaimer(): Observable<ResolvedDisclaimer | null> {
    const userLangCode = this.getBrowserLangCode();

    return this.http
      .get<DisclaimerResponse[]>(`${config.API_ENDPOINT}${ProxyEndPoints.listDisclaimers}`)
      .pipe(
        catchError(() => of([])),
        switchMap((items) => {
          const apiItems = this.filterActiveDisclaimers(items ?? []);
          const apiForUserLang = this.findApiDisclaimerByLang(apiItems, userLangCode);

          if (apiForUserLang) {
            return this.loadI18nDisclaimer(userLangCode).pipe(
              switchMap((i18nLangFields) => {
                if (i18nLangFields) {
                  return of(
                    this.buildResolvedDisclaimer(
                      userLangCode,
                      i18nLangFields,
                      apiForUserLang.disclaimerInfo,
                      apiForUserLang.createdDate,
                    ),
                  );
                }

                return this.loadI18nDisclaimer('en').pipe(
                  map((englishFields) =>
                    this.buildResolvedDisclaimer(
                      'en',
                      englishFields ?? this.defaultFields,
                      apiForUserLang.disclaimerInfo,
                      apiForUserLang.createdDate,
                    ),
                  ),
                );
              }),
            );
          }

          const apiEnglish = this.findApiDisclaimerByLang(apiItems, 'en');

          return this.loadI18nDisclaimer('en').pipe(
            map((englishFields) => {
              const fields = englishFields ?? this.defaultFields;
              return this.buildResolvedDisclaimer(
                'en',
                fields,
                apiEnglish?.disclaimerInfo ?? fields.content,
                apiEnglish?.createdDate,
              );
            }),
          );
        }),
      );
  }

  shouldRequireAcceptance(disclaimer: ResolvedDisclaimer): boolean {
    const acceptedAt = this.getAcceptedTimestamp(disclaimer.disclaimerLang);

    if (!acceptedAt) {
      return true;
    }

    return Number(disclaimer.apiCreatedDate ?? 0) > acceptedAt;
  }

  markAccepted(disclaimerLang: string): void {
    const lang = this.normalizeLang(disclaimerLang);
    if (!lang) {
      throw new Error('Disclaimer language is missing');
    }

    const current = this.getAcceptedTermsMap();
    current[lang] = Date.now();
    localStorage.setItem(this.acceptanceStorageKey, JSON.stringify(current));
  }

  private filterActiveDisclaimers(items: DisclaimerResponse[]): DisclaimerResponse[] {
    return items.filter((item) => {
      const hasLang = typeof item.disclaimerLang === 'string' && item.disclaimerLang.trim().length > 0;
      const hasContent = typeof item.disclaimerInfo === 'string' && item.disclaimerInfo.trim().length > 0;
      const isEnabled = item.disclaimerSetting !== false;
      const isActive = item.deleted !== true;
      return hasLang && hasContent && isEnabled && isActive;
    });
  }

  private findApiDisclaimerByLang(
    items: DisclaimerResponse[],
    targetLang: string,
  ): DisclaimerResponse | undefined {
    return items.find((item) => this.normalizeLang(item.disclaimerLang) === targetLang);
  }

  private loadI18nDisclaimer(lang: string): Observable<DisclaimerI18nFields | null> {
    return this.http.get<DisclaimerI18nFile>(`${this.i18nPath}/${lang}.json`).pipe(
      map((file) => {
        const disclaimer = file?.disclaimer;
        if (!disclaimer) {
          return null;
        }

        if (typeof disclaimer.content !== 'string' || disclaimer.content.trim().length === 0) {
          return null;
        }

        return {
          title: this.stringOrFallback(disclaimer.title, this.defaultFields.title),
          subHeading: this.stringOrFallback(disclaimer.subHeading, this.defaultFields.subHeading),
          content: disclaimer.content,
          buttonText: this.stringOrFallback(disclaimer.buttonText, this.defaultFields.buttonText),
          alertMessage: this.stringOrFallback(disclaimer.alertMessage, this.defaultFields.alertMessage),
        };
      }),
      catchError(() => of(null)),
    );
  }

  private buildResolvedDisclaimer(
    lang: string,
    i18nFields: DisclaimerI18nFields,
    content: string,
    apiCreatedDate?: number,
  ): ResolvedDisclaimer {
    return {
      disclaimerLang: this.normalizeLang(lang),
      title: i18nFields.title,
      subHeading: i18nFields.subHeading,
      disclaimerInfo: content,
      buttonText: i18nFields.buttonText,
      alertMessage: i18nFields.alertMessage,
      apiCreatedDate,
    };
  }

  private getBrowserLangCode(): string {
    const language =
      typeof window !== 'undefined' && window.navigator?.language
        ? window.navigator.language
        : 'en';

    const code = language.slice(0, 2).toLowerCase();
    return code || 'en';
  }

  private stringOrFallback(value: unknown, fallback: string): string {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }

    return fallback;
  }

  private getAcceptedTimestamp(disclaimerLang: string): number | null {
    const lang = this.normalizeLang(disclaimerLang);
    if (!lang) {
      return null;
    }

    const acceptedMap = this.getAcceptedTermsMap();
    const acceptedAt = acceptedMap[lang];

    return typeof acceptedAt === 'number' ? acceptedAt : null;
  }

  private getAcceptedTermsMap(): Record<string, number> {
    try {
      const raw = localStorage.getItem(this.acceptanceStorageKey);
      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      return Object.entries(parsed).reduce<Record<string, number>>((acc, [key, value]) => {
        const normalized = this.normalizeLang(key);
        if (!normalized) {
          return acc;
        }

        const timestamp = Number(value);
        if (!Number.isNaN(timestamp) && Number.isFinite(timestamp) && timestamp > 0) {
          acc[normalized] = timestamp;
        }
        return acc;
      }, {});
    } catch {
      return {};
    }
  }

  private normalizeLang(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value.toLowerCase().replace(/_/g, '-').trim().slice(0, 2);
  }
}
